<template>
    <div class="downloads-listing">
        <FilterBar
            :types="availableTypes"
            :plant-locations="availablePlantLocations"
            :search-query="searchQuery"
            :selected-type="selectedType"
            :selected-plantLocation="selectedPlantLocation"
            :show-type-filter="showTypeFilter"
            :show-plantLocation-filter="showPlantLocationFilter"
            :document-count="files.length"
            @update:search-query="searchQuery = $event"
            @update:selected-type="selectedType = $event"
            @update:selected-plantLocation="selectedPlantLocation = $event"
        />

        <!-- Screen reader announcement for search results -->
        <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            class="visually-hidden"
            :aria-label="resultAnnouncement"
        >
            {{ resultAnnouncement }}
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="downloads-listing__loading" role="status">
            <p>Downloads werden geladen...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="downloads-listing__error" role="alert">
            <p>{{ error }}</p>
            <button
                type="button"
                class="btn btn-c-primary"
                @click="fetchDownloads"
            >
                Erneut versuchen
            </button>
        </div>

        <!-- Content -->
        <template v-else>
            <section
                v-if="filteredDocuments.length > 0"
                class="downloads-listing__cards"
                aria-label="Liste der verfügbaren Downloads"
            >
                <DownloadCard
                    v-for="document in filteredDocuments"
                    :key="document.id"
                    :document="document"
                />
            </section>
            <div v-else class="downloads-listing__empty" role="status">
                <p class="downloads-listing__empty-message">
                    <span v-if="hasActiveFilters">
                        Keine Downloads gefunden, die Ihren Filterkriterien
                        entsprechen.
                    </span>
                    <span v-else> Keine Downloads verfügbar. </span>
                </p>
                <button
                    v-if="hasActiveFilters"
                    type="button"
                    class="btn btn-c-primary btn-t-outline"
                    aria-label="Alle Filter zurücksetzen und alle Downloads anzeigen"
                    @click="resetFilters"
                >
                    Filter zurücksetzen
                </button>
            </div>

        </template>
    </div>
</template>

<script>
import FilterBar from './FilterBar.vue';
import DownloadCard from './DownloadCard.vue';

export default {
    name: 'DownloadsListing',
    components: {
        FilterBar,
        DownloadCard,
    },
    data() {
        return {
            files: [],
            searchQuery: '',
            selectedType: '',
            selectedPlantLocation: '',
            loading: false,
            error: null,
            listingFilter: null, // Filter from data-listing-filter-plant_location attribute
        };
    },
    mounted() {
        // Check for data-listing-filter attribute on the component's root element or parent
        const element = this.$el;
        const filter_plantLocation_attribute =
            'data-listing-filter-plant-location';

        if (element) {
            const vueContainerEl = element.parentNode;
            if (vueContainerEl) {
                if (
                    vueContainerEl.hasAttribute(filter_plantLocation_attribute)
                ) {
                    this.listingFilter = vueContainerEl.getAttribute(
                        filter_plantLocation_attribute
                    );
                }
            }
        }

        this.fetchDownloads();
    },
    computed: {
        availableTypes() {
            const types = [...new Set(this.files.map((file) => file.type))];
            return types.sort();
        },
        availablePlantLocations() {
            // Handle both string and object plantLocation formats
            const locations = this.files
                .map((file) => {
                    const loc = file.plantLocation;
                    if (!loc) return null;
                    
                    // If it's an object, return the display string
                    if (typeof loc === 'object') {
                        return loc.display || loc.name || null;
                    }
                    
                    // If it's a string
                    return loc;
                })
                .filter(Boolean);
            
            return [...new Set(locations)].sort();
        },
        showTypeFilter() {
            return this.availableTypes.length > 1;
        },
        showPlantLocationFilter() {
            return this.availablePlantLocations.length > 1;
        },
        filteredDocuments() {
            let filtered = [...this.files];

            // Filter by document type
            if (this.selectedType) {
                filtered = filtered.filter(
                    (doc) => doc.type === this.selectedType
                );
            }

            // Filter by plantLocation (handles both string and object formats)
            if (this.selectedPlantLocation) {
                filtered = filtered.filter((doc) => {
                    const loc = doc.plantLocation;
                    if (!loc) return false;
                    
                    // If it's an object, check display, name, or code
                    if (typeof loc === 'object') {
                        return (
                            loc.display === this.selectedPlantLocation ||
                            loc.name === this.selectedPlantLocation ||
                            loc.code === this.selectedPlantLocation
                        );
                    }
                    
                    // If it's a string
                    return loc === this.selectedPlantLocation;
                });
            }

            // Filter by search query - search all text fields in the card
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter((doc) => {
                    // Get plantLocation as searchable string
                    let plantLocationText = '';
                    if (doc.plantLocation) {
                        if (typeof doc.plantLocation === 'object') {
                            plantLocationText = doc.plantLocation.display || doc.plantLocation.name || '';
                        } else {
                            plantLocationText = doc.plantLocation;
                        }
                    }
                    
                    // Search in all text fields that appear in the card
                    const searchableFields = [
                        doc.type,
                        doc.title,
                        plantLocationText,
                        doc.number,
                        doc.validUntil,
                        doc.material,
                        doc.enNormTerm,
                        doc.fileName,
                        doc.fileType,
                    ];

                    // Check if query matches any field
                    return searchableFields.some(
                        (field) =>
                            field && String(field).toLowerCase().includes(query)
                    );
                });
            }

            return filtered;
        },
        hasActiveFilters() {
            return (
                this.selectedType !== '' ||
                this.selectedPlantLocation !== '' ||
                this.searchQuery !== ''
            );
        },
        resultAnnouncement() {
            const count = this.filteredDocuments.length;
            if (count === 0) {
                if (this.hasActiveFilters) {
                    return 'Keine Downloads gefunden, die Ihren Filterkriterien entsprechen.';
                }
                return 'Keine Downloads verfügbar.';
            }
            if (count === 1) {
                return '1 Download gefunden.';
            }
            return `${count} Downloads gefunden.`;
        },
    },
    watch: {
        showTypeFilter(newVal) {
            if (!newVal) {
                this.selectedType = '';
            }
        },
        showPlantLocationFilter(newVal) {
            if (!newVal) {
                this.selectedPlantLocation = '';
            }
        },
    },
    methods: {
        async fetchDownloads() {
            this.loading = true;
            this.error = null;

            try {
                // Build API URL with optional filter
                let url = '/api/downloads?site=prostein';
                if (this.listingFilter) {
                    url += `&plantLocation=${encodeURIComponent(this.listingFilter)}`;
                }

                const response = await fetch(url);
                const result = await response.json();

                if (result.success && result.data) {
                    this.files = result.data;
                } else {
                    this.error = 'Fehler beim Laden der Downloads.';
                }
            } catch (err) {
                this.error = 'Fehler beim Laden der Downloads.';
                console.error('Error fetching downloads:', err);
            } finally {
                this.loading = false;
            }
        },
        resetFilters() {
            this.searchQuery = '';
            this.selectedType = '';
            this.selectedPlantLocation = '';
        },
    },
};
</script>
